import React from 'react';
import { Document, Page, Text, View, StyleSheet, Link } from '@react-pdf/renderer';
import { 
  commonStyles, 
  formatDate, 
  formatPhoneNumber, 
  parseHtmlToText,
  renderBulletPoints,
  isValidSummary
} from '../Services/ReactPDFService.jsx';

// Professional Template Styles
const styles = StyleSheet.create({
  ...commonStyles,
  page: {
    ...commonStyles.page,
    backgroundColor: '#ffffff',
    paddingTop: 35,
    paddingBottom: 35,
    paddingHorizontal: 40,
  },
  header: {
    marginBottom: 25,
  },
  headerTop: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 15,
  },
  nameSection: {
    flex: 1,
  },
  name: {
    fontSize: 24,
    fontWeight: 700,
    color: '#2c3e50',
    marginBottom: 5,
  },
  title: {
    fontSize: 14,
    color: '#7f8c8d',
    fontWeight: 500,
    marginTop: 10,
  },
  contactSection: {
    alignItems: 'flex-end',
  },
  contactItem: {
    fontSize: 10,
    color: '#34495e',
    marginBottom: 2,
  },
  summaryBox: {
    backgroundColor: '#f8f9fa',
    padding: 12,
    borderRadius: 2,
    marginBottom: 15,
  },
  summaryTitle: {
    fontSize: 11,
    fontWeight: 700,
    color: '#2c3e50',
    marginBottom: 6,
    textTransform: 'uppercase',
  },
  summaryText: {
    fontSize: 10,
    lineHeight: 1.5,
    color: '#2c3e50',
  },
  sectionContainer: {
    marginBottom: 15,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10,
  },
  sectionIcon: {
    width: 4,
    height: 16,
    backgroundColor: '#3498db',
    marginRight: 8,
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: '#2c3e50',
    textTransform: 'uppercase',
    letterSpacing: 0.8,
  },
  experienceItem: {
    marginBottom: 14,
    paddingLeft: 12,
  },
  achievementItem: {
    marginBottom: 0,
    paddingLeft: 12,
  },
  experienceHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 3,
  },
  jobTitle: {
    fontSize: 11,
    fontWeight: 700,
    color: '#2c3e50',
  },
  dateRange: {
    fontSize: 9,
    color: '#7f8c8d',
    fontStyle: 'italic',
  },
  companyLine: {
    flexDirection: 'row',
    marginBottom: 4,
  },
  company: {
    fontSize: 10,
    color: '#34495e',
    fontWeight: 500,
  },
  location: {
    fontSize: 10,
    color: '#7f8c8d',
    marginLeft: 5,
  },
  description: {
    fontSize: 10,
    lineHeight: 1.5,
    color: '#2c3e50',
    marginTop: 4,
  },
  bulletPoint: {
    fontSize: 10,
    marginBottom: 3,
    paddingLeft: 12,
    lineHeight: 1.4,
    color: '#2c3e50',
  },
  educationItem: {
    marginBottom: 12,
    paddingLeft: 12,
  },
  educationHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 2,
  },
  degree: {
    fontSize: 11,
    fontWeight: 700,
    color: '#2c3e50',
  },
  school: {
    fontSize: 10,
    color: '#34495e',
    marginBottom: 2,
  },
  gpa: {
    fontSize: 9,
    color: '#7f8c8d',
    fontStyle: 'italic',
  },
  skillsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    paddingLeft: 12,
  },
  skillBadge: {
    backgroundColor: '#ecf0f1',
    paddingHorizontal: 8,
    paddingVertical: 4,
    marginRight: 6,
    marginBottom: 6,
    borderRadius: 2,
  },
  skillText: {
    fontSize: 9,
    color: '#2c3e50',
  },
  skillCategory: {
    marginBottom: 1,
    marginTop: 0,
    paddingLeft: 12,
  },
  skillLabel: {
    fontSize: 10,
    fontWeight: 700,
    color: '#2c3e50',
  },
  skillCategoryText: {
    fontSize: 10,
    lineHeight: 1.4,
    color: '#2c3e50',
  },
  projectItem: {
    marginBottom: 10,
    paddingLeft: 12,
  },
  projectHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 3,
  },
  projectTitle: {
    fontSize: 11,
    fontWeight: 700,
    color: '#2c3e50',
  },
  projectTechContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginTop: 2,
  },
  projectTechLabel: {
    fontSize: 9,
    color: '#7f8c8d',
    fontStyle: 'italic',
    marginRight: 4,
  },
  projectTechText: {
    fontSize: 9,
    color: '#7f8c8d',
    fontStyle: 'italic',
    flex: 1,
    lineHeight: 1.4,
  },
  projectTech: {
    fontSize: 9,
    color: '#7f8c8d',
    fontStyle: 'italic',
    marginTop: 2,
  },
  projectDescription: {
    fontSize: 10,
    lineHeight: 1.4,
    color: '#2c3e50',
    marginTop: 3,
  },
  certificationItem: {
    marginBottom: 10,
    paddingLeft: 12,
  },
  certificationHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 2,
  },
  certName: {
    fontSize: 10,
    color: '#2c3e50',
    fontWeight: 500,
  },
  certAuthority: {
    fontSize: 9,
    color: '#7f8c8d',
  },
});

// Helper function to safely extract skill name from string or object
const getSkillName = (skill) => {
  if (typeof skill === "string") return skill;
  if (skill && typeof skill === "object") return skill.name || skill.skill || "";
  return "";
};

// Helper function to join skills array (handles both strings and objects)
const joinSkills = (skillsArray) => {
  if (!Array.isArray(skillsArray)) return "";
  return skillsArray.map(getSkillName).filter(Boolean).join(", ");
};

// Helper to get language name from string or object
const getLanguageName = (lang) => {
  if (typeof lang === "string") return lang;
  if (lang && typeof lang === "object") return lang.name || lang.language || "";
  return "";
};

// Helper to get interest name from string or object
const getInterestName = (interest) => {
  if (typeof interest === "string") return interest;
  if (interest && typeof interest === "object") return interest.name || interest.title || "";
  return "";
};

const ProfessionalTemplate = ({ resumeData, customStyles = {}, customSectionOrder = null }) => {
  const {
    personalInfo = {},
    workExperience = [],
    education = [],
    skills = {},
    projects = [],
    certifications = [],
    achievements = [],
    languages = [],
    awards = [],
    summary = '',
    professionalTitle = '',
    customSections = [],
    sectionTitles = {},
    // New sections
    volunteering = [],
    interests = [],
    industryExpertise = [],
    publications = [],
    references = [],
  } = resumeData || {};

  // Default section titles
  const defaultTitles = {
    summary: 'Professional Summary',
    experience: 'Professional Experience',
    education: 'Education',
    skills: 'Skills',
    projects: 'Projects',
    certifications: 'Certifications',
    achievements: 'Achievements',
    languages: 'Languages',
    awards: 'Awards',
    volunteering: 'Volunteering',
    interests: 'Interests',
    industryExpertise: 'Industry Expertise',
    publications: 'Publications',
    references: 'References',
  };

  // Get section title with fallback to default
  const getSectionTitle = (key) => sectionTitles[key] || defaultTitles[key];

  // Create dynamic styles based on customizations
  const createDynamicStyles = (baseStyles, customizations) => {
    // Only apply customizations if they exist, otherwise use original styles
    const hasCustomizations = customizations && Object.keys(customizations).length > 0;

    if (!hasCustomizations) {
      return baseStyles; // Return original styles unchanged
    }

    const {
      fontSize,
      sectionSpacing,
      titleColor,
      subtitleColor,
      fontFamily
    } = customizations;

    const customStyleOverrides = {};

    if (fontFamily) {
      customStyleOverrides.page = { ...baseStyles.page, fontFamily };
    }

    // Only override styles if specific customizations are provided
    if (fontSize) {
      customStyleOverrides.name = {
        ...baseStyles.name,
        fontSize: fontSize * 1.7,
      };
      customStyleOverrides.title = {
        ...baseStyles.title,
        fontSize: fontSize * 1.0,
      };
      customStyleOverrides.contactItem = {
        ...baseStyles.contactItem,
        fontSize: fontSize * 0.7,
      };
      customStyleOverrides.sectionTitle = {
        ...baseStyles.sectionTitle,
        fontSize: fontSize * 1.0,
      };
      customStyleOverrides.summaryTitle = {
        ...baseStyles.summaryTitle,
        fontSize: fontSize * 1.0,
      };
      customStyleOverrides.summaryText = {
        ...baseStyles.summaryText,
        fontSize: fontSize * 0.8,
      };
      customStyleOverrides.jobTitle = {
        ...baseStyles.jobTitle,
        fontSize: fontSize * 0.85,
      };
      customStyleOverrides.company = {
        ...baseStyles.company,
        fontSize: fontSize * 0.8,
      };
      customStyleOverrides.location = {
        ...baseStyles.location,
        fontSize: fontSize * 0.8,
      };
      customStyleOverrides.dateRange = {
        ...baseStyles.dateRange,
        fontSize: fontSize * 0.7,
      };
      customStyleOverrides.degree = {
        ...baseStyles.degree,
        fontSize: fontSize * 0.85,
      };
      customStyleOverrides.school = {
        ...baseStyles.school,
        fontSize: fontSize * 0.8,
      };
      customStyleOverrides.gpa = {
        ...baseStyles.gpa,
        fontSize: fontSize * 0.7,
      };
      customStyleOverrides.skillCategoryText = {
        ...baseStyles.skillCategoryText,
        fontSize: fontSize * 0.75,
      };
      customStyleOverrides.skillLabel = {
        ...baseStyles.skillLabel,
        fontSize: fontSize * 0.75,
      };
      customStyleOverrides.skillText = {
        ...baseStyles.skillText,
        fontSize: fontSize * 0.75,
      };
      customStyleOverrides.projectTitle = {
        ...baseStyles.projectTitle,
        fontSize: fontSize * 0.85,
      };
      customStyleOverrides.projectTechLabel = {
        ...baseStyles.projectTechLabel,
        fontSize: fontSize * 0.7,
      };
      customStyleOverrides.projectTechText = {
        ...baseStyles.projectTechText,
        fontSize: fontSize * 0.7,
      };
      customStyleOverrides.certName = {
        ...baseStyles.certName,
        fontSize: fontSize * 0.85,
      };
      customStyleOverrides.certAuthority = {
        ...baseStyles.certAuthority,
        fontSize: fontSize * 0.8,
      };
      customStyleOverrides.description = {
        ...baseStyles.description,
        fontSize: fontSize * 0.75,
      };
    }

    if (sectionSpacing) {
      customStyleOverrides.sectionTitle = {
        ...customStyleOverrides.sectionTitle || baseStyles.sectionTitle,
        marginTop: sectionSpacing,
        marginBottom: sectionSpacing * 0.5,
      };
      customStyleOverrides.sectionContainer = {
        ...baseStyles.sectionContainer,
        marginBottom: sectionSpacing,
      };
      customStyleOverrides.summaryBox = {
        ...baseStyles.summaryBox,
        marginBottom: sectionSpacing,
      };
    }

    if (titleColor) {
      customStyleOverrides.name = {
        ...customStyleOverrides.name || baseStyles.name,
        color: titleColor,
      };
      customStyleOverrides.sectionTitle = {
        ...customStyleOverrides.sectionTitle || baseStyles.sectionTitle,
        color: titleColor,
      };
      customStyleOverrides.summaryTitle = {
        ...customStyleOverrides.summaryTitle || baseStyles.summaryTitle,
        color: titleColor,
      };
    }

    if (subtitleColor) {
      customStyleOverrides.title = {
        ...customStyleOverrides.title || baseStyles.title,
        color: subtitleColor,
      };
      customStyleOverrides.jobTitle = {
        ...customStyleOverrides.jobTitle || baseStyles.jobTitle,
        color: subtitleColor,
      };
      customStyleOverrides.company = {
        ...customStyleOverrides.company || baseStyles.company,
        color: subtitleColor,
      };
      customStyleOverrides.degree = {
        ...customStyleOverrides.degree || baseStyles.degree,
        color: subtitleColor,
      };
      customStyleOverrides.school = {
        ...customStyleOverrides.school || baseStyles.school,
        color: subtitleColor,
      };
      customStyleOverrides.skillLabel = {
        ...customStyleOverrides.skillLabel || baseStyles.skillLabel,
        color: subtitleColor,
      };
      customStyleOverrides.projectTitle = {
        ...customStyleOverrides.projectTitle || baseStyles.projectTitle,
        color: subtitleColor,
      };
      customStyleOverrides.certName = {
        ...customStyleOverrides.certName || baseStyles.certName,
        color: subtitleColor,
      };
      customStyleOverrides.certAuthority = {
        ...customStyleOverrides.certAuthority || baseStyles.certAuthority,
        color: subtitleColor,
      };
    }

    return StyleSheet.create({
      ...baseStyles,
      ...customStyleOverrides,
    });
  };

  // Merge base styles with custom overrides
  const mergedStyles = createDynamicStyles(styles, customStyles);

  // Define section order and content
  const defaultSectionOrder = [
    'summary',
    'workExperience',
    'education',
    'skills',
    'projects',
    'certifications',
    'achievements',
    'languages',
    'awards',
    'volunteering',
    'interests',
    'industryExpertise',
    'publications',
    'references'
  ];


  // Build final section order - include custom sections that aren't in the order
  const customSectionIds = customSections
    .filter(cs => cs.id && cs.title && cs.items?.length > 0)
    .map(cs => cs.id);

  const baseSectionOrder = customSectionOrder || defaultSectionOrder;

  // Add custom section IDs that aren't already in the order
  const finalSectionOrder = [
    ...baseSectionOrder,
    ...customSectionIds.filter(id => !baseSectionOrder.includes(id))
  ];

  // Combine all skills into one array for fallback
  const allSkills = skills.list || [
    ...(skills.technical || []),
    ...(skills.soft || []),
    ...(skills.tools || []),
    ...(skills.other || [])
  ];

  // Define section components
  const sectionComponents = {
    summary: () => summary && isValidSummary(summary) && (
      <View style={mergedStyles.summaryBox} wrap={false}>
        <Text style={mergedStyles.summaryTitle}>Professional Summary</Text>
        <Text style={mergedStyles.summaryText}>
          {parseHtmlToText(summary)}
        </Text>
      </View>
    ),

    workExperience: () => workExperience.length > 0 && (
      <View style={mergedStyles.sectionContainer}>
        <View style={mergedStyles.sectionHeader} minPresenceAhead={50}>
          <View style={mergedStyles.sectionIcon} />
          <Text style={mergedStyles.sectionTitle}>{getSectionTitle('experience')}</Text>
        </View>
        {workExperience.map((job, index) => (
          <View key={index} style={mergedStyles.experienceItem} wrap={false}>
            <View style={mergedStyles.experienceHeader}>
              <Text style={mergedStyles.jobTitle}>{job.jobTitle}</Text>
              <Text style={mergedStyles.dateRange}>
                {formatDate(job.startDate)} - {(job.isCurrentJob || job.endDate === 'Present' || job.endDate === 'Current' || !job.endDate) ? 'Present' : formatDate(job.endDate)}
              </Text>
            </View>
            <View style={mergedStyles.companyLine}>
              <Text style={mergedStyles.company}>{job.company}</Text>
              {job.location && (
                <Text style={mergedStyles.location}>• {job.location}</Text>
              )}
            </View>
            {job.description && renderBulletPoints(parseHtmlToText(job.description), mergedStyles)}
          </View>
        ))}
      </View>
    ),

    education: () => education.length > 0 && (
      <View style={mergedStyles.sectionContainer}>
        <View style={mergedStyles.sectionHeader} minPresenceAhead={50}>
          <View style={mergedStyles.sectionIcon} />
          <Text style={mergedStyles.sectionTitle}>{getSectionTitle('education')}</Text>
        </View>
        {education.map((edu, index) => (
          <View key={index} style={mergedStyles.educationItem} wrap={false}>
            <View style={mergedStyles.educationHeader}>
              <Text style={mergedStyles.degree}>
                {edu.degree} {edu.fieldOfStudy && `in ${edu.fieldOfStudy}`}
              </Text>
              <Text style={mergedStyles.dateRange}>
                {formatDate(edu.startDate)} - {(edu.endDate === 'Present' || edu.endDate === 'Current' || !edu.endDate) ? 'Present' : formatDate(edu.endDate)}
              </Text>
            </View>
            <View style={mergedStyles.companyLine}>
              <Text style={mergedStyles.school}>{edu.school}</Text>
              {edu.location && (
                <Text style={mergedStyles.location}>• {edu.location}</Text>
              )}
            </View>
            {edu.gpa && (
              <Text style={mergedStyles.gpa}>GPA: {edu.gpa}</Text>
            )}
          </View>
        ))}
      </View>
    ),

    skills: () => (skills.technical?.length > 0 || skills.soft?.length > 0 || skills.tools?.length > 0 || skills.other?.length > 0 || skills.languages?.length > 0 || skills.frameworks?.length > 0 || skills.developer_tools?.length > 0 || allSkills.length > 0) && (
      <View style={mergedStyles.sectionContainer} wrap={false}>
        <View style={mergedStyles.sectionHeader}>
          <View style={mergedStyles.sectionIcon} />
          <Text style={mergedStyles.sectionTitle}>{getSectionTitle('skills')}</Text>
        </View>
        {/* Check if we have categorized skills */}
        {(skills.technical?.length > 0 || skills.soft?.length > 0 || skills.tools?.length > 0 || skills.other?.length > 0 || skills.languages?.length > 0 || skills.frameworks?.length > 0 || skills.developer_tools?.length > 0) ? (
          <View>
            {skills.technical?.length > 0 && (
              <View style={mergedStyles.skillCategory}>
                <Text style={mergedStyles.skillCategoryText}>
                  <Text style={mergedStyles.skillLabel}>Technical Skills: </Text>
                  {joinSkills(skills.technical)}
                </Text>
              </View>
            )}
            {skills.soft?.length > 0 && (
              <View style={mergedStyles.skillCategory}>
                <Text style={mergedStyles.skillCategoryText}>
                  <Text style={mergedStyles.skillLabel}>Soft Skills: </Text>
                  {joinSkills(skills.soft)}
                </Text>
              </View>
            )}
            {skills.tools?.length > 0 && (
              <View style={mergedStyles.skillCategory}>
                <Text style={mergedStyles.skillCategoryText}>
                  <Text style={mergedStyles.skillLabel}>Tools: </Text>
                  {joinSkills(skills.tools)}
                </Text>
              </View>
            )}
            {skills.languages?.length > 0 && (
              <View style={mergedStyles.skillCategory}>
                <Text style={mergedStyles.skillCategoryText}>
                  <Text style={mergedStyles.skillLabel}>Languages: </Text>
                  {joinSkills(skills.languages)}
                </Text>
              </View>
            )}
            {skills.frameworks?.length > 0 && (
              <View style={mergedStyles.skillCategory}>
                <Text style={mergedStyles.skillCategoryText}>
                  <Text style={mergedStyles.skillLabel}>Frameworks: </Text>
                  {joinSkills(skills.frameworks)}
                </Text>
              </View>
            )}
            {skills.developer_tools?.length > 0 && (
              <View style={mergedStyles.skillCategory}>
                <Text style={mergedStyles.skillCategoryText}>
                  <Text style={mergedStyles.skillLabel}>Developer Tools: </Text>
                  {joinSkills(skills.developer_tools)}
                </Text>
              </View>
            )}
            {skills.other?.length > 0 && (
              <View style={mergedStyles.skillCategory}>
                <Text style={mergedStyles.skillCategoryText}>
                  <Text style={mergedStyles.skillLabel}>Other: </Text>
                  {joinSkills(skills.other)}
                </Text>
              </View>
            )}
          </View>
        ) : (
          // Fallback to badge format for flat skills
          <View style={mergedStyles.skillsGrid}>
            {allSkills.map((skill, index) => (
              <View key={index} style={mergedStyles.skillBadge}>
                <Text style={mergedStyles.skillText}>{getSkillName(skill)}</Text>
              </View>
            ))}
          </View>
        )}
      </View>
    ),

    projects: () => projects.length > 0 && (
      <View style={mergedStyles.sectionContainer}>
        <View style={mergedStyles.sectionHeader} minPresenceAhead={50}>
          <View style={mergedStyles.sectionIcon} />
          <Text style={mergedStyles.sectionTitle}>{getSectionTitle('projects')}</Text>
        </View>
        {projects.map((project, index) => (
          <View key={index} style={mergedStyles.projectItem} wrap={false}>
            <View style={mergedStyles.projectHeader}>
              <Text style={mergedStyles.projectTitle}>{project.name}</Text>
              {(project.url || project.link) && (
                <Link
                  src={(project.url || project.link).startsWith('http') ?
                    (project.url || project.link) :
                    `https://${project.url || project.link}`
                  }
                  style={{
                    fontSize: mergedStyles.projectTech.fontSize,
                    color: '#3498db',
                    marginLeft: 4,
                    textDecoration: 'underline',
                    fontWeight: 'bold'
                  }}
                >
                  [LINK]
                </Link>
              )}
            </View>
            {project.technologies && (
              <View style={mergedStyles.projectTechContainer}>
                <Text style={mergedStyles.projectTechLabel}>Technologies:</Text>
                <Text style={mergedStyles.projectTechText}>{project.technologies}</Text>
              </View>
            )}
            {project.description && (
              <View style={{ marginTop: 4 }}>
                {renderBulletPoints(parseHtmlToText(project.description), mergedStyles)}
              </View>
            )}
          </View>
        ))}
      </View>
    ),

    certifications: () => certifications.length > 0 && (
      <View style={mergedStyles.sectionContainer}>
        <View style={mergedStyles.sectionHeader} minPresenceAhead={50}>
          <View style={mergedStyles.sectionIcon} />
          <Text style={mergedStyles.sectionTitle}>{getSectionTitle('certifications')}</Text>
        </View>
        {certifications.map((cert, index) => (
          <View key={index} style={{ flexDirection: 'row', marginBottom: 4 }} wrap={false}>
            <Text style={{ fontSize: 10, color: '#2c3e50', marginRight: 6, lineHeight: 1.5 }}>•</Text>
            <Text style={{ fontSize: 10, color: '#333333', lineHeight: 1.5, flex: 1 }}>
              {cert.name}
              {(cert.authority || cert.issuer) && ` - ${cert.authority || cert.issuer}`}
              {cert.date && ` (${formatDate(cert.date)})`}
            </Text>
          </View>
        ))}
      </View>
    ),

    achievements: () => achievements.length > 0 && (
      <View style={mergedStyles.sectionContainer}>
        <View style={mergedStyles.sectionHeader} minPresenceAhead={50}>
          <View style={mergedStyles.sectionIcon} />
          <Text style={mergedStyles.sectionTitle}>{getSectionTitle('achievements')}</Text>
        </View>
        {achievements.map((achievement, index) => (
          <View key={index} style={mergedStyles.achievementItem} wrap={false}>
            {achievement.description && (
              <View style={{ marginTop: 2, paddingLeft: 12 }}>
                {renderBulletPoints(parseHtmlToText(achievement.description), mergedStyles)}
              </View>
            )}
          </View>
        ))}
      </View>
    ),

    languages: () => languages.length > 0 && (
      <View style={mergedStyles.sectionContainer} wrap={false}>
        <View style={mergedStyles.sectionHeader} minPresenceAhead={50}>
          <View style={mergedStyles.sectionIcon} />
          <Text style={mergedStyles.sectionTitle}>{getSectionTitle('languages')}</Text>
        </View>
        <View style={mergedStyles.skillsGrid}>
          {languages.map((lang, index) => (
            <View key={index} style={mergedStyles.skillBadge}>
              <Text style={mergedStyles.skillText}>
                {getLanguageName(lang)}{lang.proficiency ? ` - ${lang.proficiency}` : ''}
              </Text>
            </View>
          ))}
        </View>
      </View>
    ),

    awards: () => awards.length > 0 && (
      <View style={mergedStyles.sectionContainer}>
        <View style={mergedStyles.sectionHeader} minPresenceAhead={50}>
          <View style={mergedStyles.sectionIcon} />
          <Text style={mergedStyles.sectionTitle}>{getSectionTitle('awards')}</Text>
        </View>
        {awards.map((award, index) => (
          <View key={index} style={mergedStyles.certificationItem} wrap={false}>
            <View style={mergedStyles.certificationHeader}>
              <Text style={mergedStyles.certName}>{award.title}</Text>
              {award.date && (
                <Text style={mergedStyles.dateRange}>
                  {formatDate(award.date)}
                </Text>
              )}
            </View>
            {award.issuer && (
              <Text style={mergedStyles.certAuthority}>{award.issuer}</Text>
            )}
            {award.description && (
              <View style={{ marginTop: 4 }}>
                {renderBulletPoints(parseHtmlToText(award.description), mergedStyles)}
              </View>
            )}
          </View>
        ))}
      </View>
    ),

    // Volunteering section
    volunteering: () => volunteering.length > 0 && (
      <View style={mergedStyles.sectionContainer}>
        <View style={mergedStyles.sectionHeader} minPresenceAhead={50}>
          <View style={mergedStyles.sectionIcon} />
          <Text style={mergedStyles.sectionTitle}>{getSectionTitle('volunteering')}</Text>
        </View>
        {volunteering.map((vol, index) => (
          <View key={index} style={mergedStyles.experienceItem} wrap={false}>
            <View style={mergedStyles.experienceHeader}>
              <Text style={mergedStyles.jobTitle}>{vol.role}</Text>
              <Text style={mergedStyles.dateRange}>
                {formatDate(vol.startDate)} - {vol.isCurrentRole ? 'Present' : formatDate(vol.endDate)}
              </Text>
            </View>
            <View style={mergedStyles.companyLine}>
              <Text style={mergedStyles.company}>{vol.organization}</Text>
              {vol.location && (
                <Text style={mergedStyles.location}>• {vol.location}</Text>
              )}
            </View>
            {vol.description && renderBulletPoints(parseHtmlToText(vol.description), mergedStyles)}
          </View>
        ))}
      </View>
    ),

    // Interests section
    interests: () => interests.length > 0 && (
      <View style={mergedStyles.sectionContainer} wrap={false}>
        <View style={mergedStyles.sectionHeader} minPresenceAhead={50}>
          <View style={mergedStyles.sectionIcon} />
          <Text style={mergedStyles.sectionTitle}>{getSectionTitle('interests')}</Text>
        </View>
        <View style={mergedStyles.skillsGrid}>
          {interests.map((interest, index) => (
            <View key={index} style={mergedStyles.skillBadge}>
              <Text style={mergedStyles.skillText}>{getInterestName(interest)}</Text>
            </View>
          ))}
        </View>
      </View>
    ),

    // Industry Expertise section
    industryExpertise: () => industryExpertise.length > 0 && (
      <View style={mergedStyles.sectionContainer}>
        <View style={mergedStyles.sectionHeader} minPresenceAhead={50}>
          <View style={mergedStyles.sectionIcon} />
          <Text style={mergedStyles.sectionTitle}>{getSectionTitle('industryExpertise')}</Text>
        </View>
        {industryExpertise.map((exp, index) => (
          <View key={index} style={mergedStyles.certificationItem} wrap={false}>
            <View style={mergedStyles.certificationHeader}>
              <Text style={mergedStyles.certName}>{exp.name}</Text>
              {exp.proficiency && (
                <Text style={mergedStyles.dateRange}>{exp.proficiency}</Text>
              )}
            </View>
            {exp.yearsOfExperience && (
              <Text style={mergedStyles.certAuthority}>{exp.yearsOfExperience} years experience</Text>
            )}
          </View>
        ))}
      </View>
    ),

    // Publications section
    publications: () => publications.length > 0 && (
      <View style={mergedStyles.sectionContainer}>
        <View style={mergedStyles.sectionHeader} minPresenceAhead={50}>
          <View style={mergedStyles.sectionIcon} />
          <Text style={mergedStyles.sectionTitle}>{getSectionTitle('publications')}</Text>
        </View>
        {publications.map((pub, index) => (
          <View key={index} style={mergedStyles.certificationItem} wrap={false}>
            <View style={mergedStyles.certificationHeader}>
              <Text style={mergedStyles.certName}>{pub.title}</Text>
              {pub.date && (
                <Text style={mergedStyles.dateRange}>{formatDate(pub.date)}</Text>
              )}
            </View>
            {pub.journal && (
              <Text style={mergedStyles.certAuthority}>{pub.journal}</Text>
            )}
            {pub.authors && pub.authors.length > 0 && (
              <Text style={mergedStyles.certAuthority}>{pub.authors.join(', ')}</Text>
            )}
            {pub.description && (
              <View style={{ marginTop: 4 }}>
                {renderBulletPoints(parseHtmlToText(pub.description), mergedStyles)}
              </View>
            )}
          </View>
        ))}
      </View>
    ),

    // References section
    references: () => references.length > 0 && (
      <View style={mergedStyles.sectionContainer}>
        <View style={mergedStyles.sectionHeader} minPresenceAhead={50}>
          <View style={mergedStyles.sectionIcon} />
          <Text style={mergedStyles.sectionTitle}>{getSectionTitle('references')}</Text>
        </View>
        {references.map((ref, index) => (
          <View key={index} style={mergedStyles.certificationItem} wrap={false}>
            <Text style={mergedStyles.certName}>{ref.name}</Text>
            {(ref.title || ref.company) && (
              <Text style={mergedStyles.certAuthority}>
                {ref.title}{ref.title && ref.company ? ' at ' : ''}{ref.company}
              </Text>
            )}
            {(ref.email || ref.phone) && (
              <Text style={{ fontSize: 9, color: '#7f8c8d', fontStyle: 'italic' }}>
                {[ref.email, ref.phone].filter(Boolean).join(' • ')}
              </Text>
            )}
          </View>
        ))}
      </View>
    ),

    // Dynamic custom sections - each custom section gets its own render function
    ...customSections.reduce((acc, customSection) => {
      if (customSection.id && customSection.title && customSection.items?.length > 0) {
        acc[customSection.id] = () => (
          <View style={mergedStyles.sectionContainer} wrap={false}>
            <View style={mergedStyles.sectionHeader}>
              <View style={mergedStyles.sectionIcon} />
              <Text style={mergedStyles.sectionTitle}>{customSection.title}</Text>
            </View>
            {customSection.items.map((item, itemIndex) => (
              <View key={itemIndex} style={mergedStyles.certificationItem}>
                {item.content && (
                  <View style={mergedStyles.descriptionContainer}>
                    {renderBulletPoints(item.content, mergedStyles)}
                  </View>
                )}
              </View>
            ))}
          </View>
        );
      }
      return acc;
    }, {})
  };

  return (
    <Document>
      <Page size="A4" style={mergedStyles.page} wrap={true}>
        {/* Header Section - Always stays at top */}
        <View style={mergedStyles.header}>
          <View style={mergedStyles.headerTop}>
            <View style={mergedStyles.nameSection}>
              <Text style={mergedStyles.name}>
                {personalInfo.firstName} {personalInfo.lastName}
              </Text>
              {(professionalTitle || (workExperience.length > 0 && workExperience[0].jobTitle)) && (
                <Text style={mergedStyles.title}>
                  {professionalTitle || workExperience[0].jobTitle}
                </Text>
              )}
            </View>
            <View style={mergedStyles.contactSection}>
              {personalInfo.email && (
                <Text style={mergedStyles.contactItem}>{personalInfo.email}</Text>
              )}
              {personalInfo.phone && (
                <Text style={mergedStyles.contactItem}>{formatPhoneNumber(personalInfo.phone)}</Text>
              )}
              {personalInfo.location && (
                <Text style={mergedStyles.contactItem}>{personalInfo.location}</Text>
              )}
              {personalInfo.linkedinUrl && (
                <Text style={mergedStyles.contactItem}>
                  linkedin.com/in/{personalInfo.linkedinUrl.split('/').pop()}
                </Text>
              )}
              {personalInfo.githubUrl && (
                <Text style={mergedStyles.contactItem}>
                  github.com/{personalInfo.githubUrl.split('/').pop()}
                </Text>
              )}
            </View>
          </View>
        </View>

        {/* Render sections in custom order */}
        {finalSectionOrder.map((sectionId, index) => {
          const SectionComponent = sectionComponents[sectionId];
          return SectionComponent ? (
            <View key={`${sectionId}-${index}`}>
              {SectionComponent()}
            </View>
          ) : null;
        })}

      </Page>
    </Document>
  );
};

export default ProfessionalTemplate;